import { Autocomplete, Box, TextField, Typography } from '@mui/material';
import {
  SUBPROJECT_ADDRESS_MAX_LENGTH,
  type AddressOption,
} from './subprojectAddress';

interface SubprojectAddressFieldProps {
  value: string;
  /** Called only for user actions: typing, picking an option, clearing. */
  onChange: (address: string) => void;
  options: AddressOption[];
}

export const ENTER_NEW_ADDRESS_LABEL = 'Enter a new address…';

// Last dropdown row; picking it empties the field, ready for typing. Its
// label is empty, so MUI's filter keeps it while the input is untouched and
// drops it as soon as the user types (they are already entering one).
const ENTER_NEW: AddressOption = { address: '', source: '' };

const optionLabel = (option: string | AddressOption) =>
  typeof option === 'string' ? option : option.address;

/**
 * Free-text address input with a dropdown of the addresses already on the
 * project. Picking an option fills the field; typing anything else, or
 * picking "Enter a new address…", is the new-address path — the value is
 * always a plain string.
 *
 * Because `value` and `inputValue` are the same string, MUI treats an untyped
 * input as the selected value and lists every option on open, so a pre-filled
 * field still offers the other addresses; typing then filters them.
 */
export function SubprojectAddressField({
  value,
  onChange,
  options,
}: SubprojectAddressFieldProps) {
  return (
    <Autocomplete<AddressOption, false, false, true>
      freeSolo
      options={options.length > 0 ? [...options, ENTER_NEW] : options}
      // null when empty, so MUI does not count the field as dirty and show
      // the clear button
      value={value || null}
      inputValue={value}
      forcePopupIcon={options.length > 0}
      getOptionLabel={optionLabel}
      isOptionEqualToValue={(option, val) =>
        option.address === optionLabel(val)
      }
      // picks and clears; typing is reported by onInputChange
      onChange={(_, newValue) =>
        onChange(newValue ? optionLabel(newValue) : '')
      }
      onInputChange={(_, text, reason) => {
        if (reason === 'input') onChange(text);
      }}
      renderOption={(props, option) => {
        const { key, ...optionProps } = props as typeof props & {
          key: string;
        };
        if (option === ENTER_NEW) {
          return (
            <Box component='li' key={key} {...optionProps}>
              <Typography variant='body2' sx={{ fontStyle: 'italic' }}>
                {ENTER_NEW_ADDRESS_LABEL}
              </Typography>
            </Box>
          );
        }
        return (
          <Box component='li' key={key} {...optionProps}>
            <Box>
              <Typography variant='body2'>{option.address}</Typography>
              <Typography variant='caption' color='text.secondary'>
                {option.source}
              </Typography>
            </Box>
          </Box>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          fullWidth
          variant='standard'
          placeholder='Select an address or type a new one'
          inputProps={{
            ...params.inputProps,
            maxLength: SUBPROJECT_ADDRESS_MAX_LENGTH,
          }}
          sx={{
            '& .MuiInputBase-input': { color: 'primary.main' },
          }}
        />
      )}
    />
  );
}
